// @ts-nocheck
/**
 * Supabase Adapter - Real implementation
 */

import { supabase } from "@/integrations/supabase/client";
import { getCurrentTenantId } from "@/lib/tenant";
import { createUserMessage } from "@/lib/errorHandler";
import type {
  ProductsPort,
  StockPort,
  BatchesPort,
  SalesPort,
  IngredientsPort,
  UsersPort,
  ReportsPort,
  CategoriesPort,
  UnitsPort,
  EntradaPort,
  EstoquePort,
  TipoProdutosPort,
  EstabelecimentosPort,
  ClientesPort,
  VendasPort,
  ReceitasPort,
  FornadasPort,
  QueryOptions,
  CreateOptions,
  ListResponse,
  IngredientWithDetails,
} from "../ports";
import type { StockItem, MovementHistory } from "@/types/stock";

/**
 * Handle Supabase errors with user-friendly messages
 */
function handleSupabaseError(error: any): never {
  const userMessage = createUserMessage(error);
  throw new Error(userMessage);
}

// ============================================================================
// PRODUCTS ADAPTER
// ============================================================================

export const productsAdapter: ProductsPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "created_at",
      order = "desc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("produto")
      .select(
        `
        *,
        categoria:id_categoria (
          id_categoria,
          nome
        )
      `,
        { count: "exact" }
      );

    if (search) {
      query = query.ilike("nome", `%${search}%`);
    }

    if (filters.categoria) {
      query = query.eq("id_categoria", filters.categoria);
    }
    if (filters.perecivel !== undefined) {
      query = query.eq("perecivel", filters.perecivel);
    }

    query = query.order(sort, { ascending: order === "asc" });

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("produto")
      .select(
        `
        *,
        categoria:id_categoria (nome)
      `
      )
      .eq("id_produto", id)
      .maybeSingle() as any;

    if (error) handleSupabaseError(error);
    if (!data) throw new Error("Produto não encontrado");

    return data;
  },

  async create(input: any, options?: CreateOptions) {
    console.log("🆕 [ProductAdapter] Criando produto:", input);
    
    const tenantId = await getCurrentTenantId();
    console.log("🏢 [ProductAdapter] Tenant ID:", tenantId);
    
    const payload = {
      nome: input.name,
      id_categoria: input.category,
      unidade_padrao: input.unidadePadrao,
      tenant_id: tenantId,
      perecivel: input.perishable,
      controla_lote: input.controlLot,
      controla_validade: input.controlExpiry,
      quantidade_minima: input.minQuantity || 0,
      observacoes: input.notes,
    };
    
    console.log("📦 [ProductAdapter] Payload INSERT:", payload);
    
    const { data, error } = await supabase
      .from("produto")
      .insert(payload as any)
      .select(
        `
        *,
        categoria:id_categoria (nome)
      `
      )
      .single() as any;

    if (error) {
      console.error("❌ [ProductAdapter] Erro ao criar produto:", {
        error,
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
      });
      handleSupabaseError(error);
    }
    
    console.log("✅ [ProductAdapter] Produto criado com sucesso:", data);
    return data;
  },

  async update(id: string, input: any) {
    const { data, error } = await supabase
      .from("produto")
      .update({
        nome: input.name,
        id_categoria: input.category,
        unidade_padrao: input.unidadePadrao,
        perecivel: input.perishable,
        controla_lote: input.controlLot,
        controla_validade: input.controlExpiry,
        quantidade_minima: input.minQuantity || 0,
        observacoes: input.notes,
      })
      .eq("id_produto", id)
      .select(
        `
        *,
        categoria:id_categoria (nome)
      `
      )
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async delete(id: string) {
    try {
      // 1️⃣ VALIDAR se o produto está em uso
      const { data: checkResult, error: checkError } = await supabase
        .rpc('check_produto_em_uso', { p_produto_id: id });

      if (checkError) {
        console.error('Erro ao verificar uso do produto:', checkError);
        throw new Error('Erro ao verificar se o produto pode ser excluído. Tente novamente.');
      }

      // 2️⃣ Se estiver em uso, bloquear exclusão
      if (checkResult && checkResult.length > 0 && checkResult[0].em_uso) {
        const { total_entradas, total_receitas } = checkResult[0];
        
        // Construir mensagem detalhada
        let errorMsg = 'Este produto não pode ser excluído porque está sendo usado em:\n\n';
        
        if (total_entradas > 0) {
          errorMsg += `• ${total_entradas} entrada(s) de estoque\n`;
        }
        
        if (total_receitas > 0) {
          errorMsg += `• ${total_receitas} receita(s)\n`;
        }
        
        errorMsg += '\nPara excluir este produto, primeiro remova-o de todas as entradas e receitas.';
        
        throw new Error(errorMsg);
      }

      // 3️⃣ Se NÃO estiver em uso, deletar permanentemente
      const { error: deleteError } = await supabase
        .from("produto")
        .delete()
        .eq("id_produto", id);

      if (deleteError) {
        handleSupabaseError(deleteError);
      }
      
    } catch (error) {
      // Se for erro customizado, repassar
      if (error instanceof Error) {
        throw error;
      }
      // Caso contrário, erro genérico
      throw new Error('Erro ao excluir produto. Tente novamente.');
    }
  },

  async getProdutoCustosUnitarios(produtoIds: string[]) {
    if (!produtoIds || produtoIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from("vw_produto_custo_unitario")
      .select("*")
      .in("produto_id", produtoIds);

    if (error) handleSupabaseError(error);
    return data || [];
  },
};

// ============================================================================
// CATEGORIES ADAPTER
// ============================================================================

export const categoriesAdapter: CategoriesPort = {
  async list(options?: QueryOptions) {
    const { filters = {} } = options || {};

    let query = supabase.from("categoria").select("*", { count: "exact" });

    if (filters.ativa !== undefined) {
      query = query.eq("ativa", filters.ativa);
    } else {
      query = query.eq("ativa", true);
    }

    query = query.order("ordem", { ascending: true });

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page: 1,
      limit: 100,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("categoria")
      .select("*")
      .eq("id_categoria", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    if (!data) throw new Error("Categoria não encontrada");

    return data;
  },

  async create(input: any) {
    const tenant_id = await getCurrentTenantId();
    
    const { data: maxOrdemData } = await supabase
      .from("categoria")
      .select("ordem")
      .eq("tenant_id", tenant_id)
      .order("ordem", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    const proximaOrdem = (maxOrdemData?.ordem ?? 0) + 1;
    
    const { data, error } = await supabase
      .from("categoria")
      .insert({
        nome: input.nome?.trim(),
        ordem: proximaOrdem,
        ativa: input.ativa !== undefined ? input.ativa : true,
        tenant_id,
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { data, error } = await supabase
      .from("categoria")
      .update({
        nome: input.nome?.trim(),
        ordem: input.ordem,
        ativa: input.ativa,
      })
      .eq("id_categoria", id)
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("categoria")
      .delete()
      .eq("id_categoria", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// UNITS ADAPTER
// ============================================================================

export const unitsAdapter: UnitsPort = {
  async list(options?: QueryOptions) {
    const { filters = {} } = options || {};

    let query = supabase.from("unidade_medida").select("*", { count: "exact" });

    if (filters.tipo) {
      query = query.eq("tipo", filters.tipo);
    }

    query = query.order("sigla", { ascending: true });

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page: 1,
      limit: 100,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("unidade_medida")
      .select("*")
      .eq("id_unidade", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    if (!data) throw new Error("Unidade não encontrada");

    return data;
  },
};

// ============================================================================
// STUBS FOR OTHER ADAPTERS
// ============================================================================

const notImplemented = () => {
  throw new Error("Adapter não implementado ainda. Esta funcionalidade estará disponível em breve.");
};

export const stockAdapter: StockPort = {
  current: notImplemented,
  adjust: notImplemented,
};

export const batchesAdapter: BatchesPort = {
  create: notImplemented,
  list: notImplemented,
  get: notImplemented,
};

export const salesAdapter: SalesPort = {
  create: notImplemented,
  list: notImplemented,
  get: notImplemented,
};

// ============================================================================
// ENTRADA ADAPTER
// ============================================================================

export const entradaAdapter: EntradaPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "created_at",
      order = "desc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("estoque_entrada")
      .select(
        `
        *,
          fornecedor!fk_estoque_entrada_fornecedor(nome),
          itens:estoque_entrada_item(
            *,
            produto(unidade_padrao, nome),
            marca:marca_id(id_marca, nome)
          )
      `,
        { count: "exact" }
      );

    if (filters.tipo_movimento) {
      query = query.eq("tipo_movimento", filters.tipo_movimento);
    }
    if (filters.data_inicio) {
      query = query.gte("data_entrada", filters.data_inicio);
    }
    if (filters.data_fim) {
      query = query.lte("data_entrada", filters.data_fim);
    }

    if (search) {
      query = query.or(
        `fornecedor_nome.ilike.%${search}%,documento_numero.ilike.%${search}%`
      );
    }

    query = query.order(sort, { ascending: order === "asc" });
    const from = (page - 1) * limit;
    query = query.range(from, from + limit - 1);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    const items = (data || []).map((row: any) => ({
      id: row.entrada_id,
      cabecalho: {
        tipo_movimento: row.tipo_movimento,
        data_entrada: row.data_entrada,
        fornecedor_id: row.fornecedor_id,
        fornecedor_nome: row.fornecedor?.nome || row.fornecedor_nome,
        documento_tipo: row.documento_tipo,
        documento_numero: row.documento_numero,
        documento_serie: row.documento_serie,
        chave_nfe: row.chave_nfe,
        observacao: row.observacao,
      },
      itens: (row.itens || []).map((item: any) => {
        const { produto, marca, ...itemSemRelacoes } = item;
        return {
          ...itemSemRelacoes,
          unidade_padrao: produto?.unidade_padrao || null,
          produto_nome: produto?.nome || item.produto_nome,
          marca: marca?.nome || item.marca,
        };
      }),
      totais: {
        subtotal_compra: row.subtotal_compra,
        desconto_total_valor: row.desconto_total_valor,
        desconto_total_pct: row.desconto_total_pct,
        frete_total: row.frete_total,
        outras_despesas_total: row.outras_despesas_total,
        impostos_nao_recuperaveis_total: row.impostos_nao_recuperaveis_total,
        total_geral_documento: row.total_geral_documento,
        estrategia_rateio: row.estrategia_rateio,
      },
      created_at: row.created_at,
      updated_at: row.updated_at,
    }));

    return {
      items,
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("estoque_entrada")
      .select(
        `
        *,
          fornecedor!fk_estoque_entrada_fornecedor(nome),
          itens:estoque_entrada_item(
            *,
            produto(unidade_padrao, nome),
            marca:marca_id(id_marca, nome)
          )
      `
      )
      .eq("entrada_id", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    if (!data) return null;

    return {
      id: data.entrada_id,
      cabecalho: {
        tipo_movimento: data.tipo_movimento,
        data_entrada: data.data_entrada,
        fornecedor_id: data.fornecedor_id,
        fornecedor_nome: data.fornecedor?.nome || data.fornecedor_nome,
        documento_tipo: data.documento_tipo,
        documento_numero: data.documento_numero,
        documento_serie: data.documento_serie,
        chave_nfe: data.chave_nfe,
        observacao: data.observacao,
      },
      itens: (data.itens || []).map((item: any) => {
        const { produto, marca, ...itemSemRelacoes } = item;
        return {
          ...itemSemRelacoes,
          unidade_padrao: produto?.unidade_padrao || null,
          produto_nome: produto?.nome || item.produto_nome,
          marca: marca?.nome || item.marca,
        };
      }),
      totais: {
        subtotal_compra: data.subtotal_compra,
        desconto_total_valor: data.desconto_total_valor,
        desconto_total_pct: data.desconto_total_pct,
        frete_total: data.frete_total,
        outras_despesas_total: data.outras_despesas_total,
        impostos_nao_recuperaveis_total: data.impostos_nao_recuperaveis_total,
        total_geral_documento: data.total_geral_documento,
        estrategia_rateio: data.estrategia_rateio,
      },
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  },

  async create(entrada: any) {
    const { data: userData } = await supabase.auth.getUser();
    const tenantId = await getCurrentTenantId();

    const { data: entradaData, error: entradaError } = await supabase
      .from("estoque_entrada")
      .insert({
        entrada_id: entrada.id,
        tenant_id: tenantId,
        tipo_movimento: entrada.cabecalho.tipo_movimento,
        data_entrada: entrada.cabecalho.data_entrada,
        fornecedor_id: entrada.cabecalho.fornecedor_id,
        fornecedor_nome: entrada.cabecalho.fornecedor_nome,
        documento_tipo: entrada.cabecalho.documento_tipo,
        documento_numero: entrada.cabecalho.documento_numero,
        documento_serie: entrada.cabecalho.documento_serie,
        chave_nfe: entrada.cabecalho.chave_nfe,
        observacao: entrada.cabecalho.observacao,
        outras_despesas_total: entrada.totais.outras_despesas_total,
        total_geral_documento: entrada.totais.total_geral_documento,
        created_by: userData.user?.id,
      })
      .select()
      .single();

    if (entradaError) handleSupabaseError(entradaError);

    if (entrada.itens.length > 0) {
      const itensData = entrada.itens.map((item: any) => ({
        entrada_item_id: item.id,
        entrada_id: entradaData.entrada_id,
        tenant_id: tenantId,
        produto_id: item.produto_id,
        produto_nome: item.produto_nome,
        quantidade_compra: item.quantidade_compra,
        custo_unitario_compra: item.custo_unitario_compra,
        outras_despesas_item: item.outras_despesas_item,
        lote: item.lote,
        validade: item.validade,
        localizacao: item.localizacao,
        observacao_item: item.observacao_item,
        marca: item.marca || null,
        forma_pagamento: item.forma_pagamento || null,
        tipo_embalagem: item.tipo_embalagem,
        peso_individual_g: item.peso_individual_g || null,
        volume_individual_ml: item.volume_individual_ml || null,
      }));

      const { error: itensError } = await supabase
        .from("estoque_entrada_item")
        .insert(itensData);

      if (itensError) handleSupabaseError(itensError);
    }

    console.log("📦 [EntradaAdapter] Entrada criada com sucesso:", entradaData.entrada_id);
    return { id: entradaData.entrada_id };
  },

  async update(id: string, entrada: any) {
    const updateData: any = {};

    if (entrada.cabecalho) {
      Object.assign(updateData, {
        tipo_movimento: entrada.cabecalho.tipo_movimento,
        data_entrada: entrada.cabecalho.data_entrada,
        fornecedor_id: entrada.cabecalho.fornecedor_id,
        fornecedor_nome: entrada.cabecalho.fornecedor_nome,
        documento_tipo: entrada.cabecalho.documento_tipo,
        documento_numero: entrada.cabecalho.documento_numero,
        documento_serie: entrada.cabecalho.documento_serie,
        chave_nfe: entrada.cabecalho.chave_nfe,
        observacao: entrada.cabecalho.observacao,
      });
    }

    if (entrada.totais) {
      Object.assign(updateData, {
        outras_despesas_total: entrada.totais.outras_despesas_total,
        total_geral_documento: entrada.totais.total_geral_documento,
      });
    }

    const { error } = await supabase
      .from("estoque_entrada")
      .update(updateData)
      .eq("entrada_id", id);

    if (error) handleSupabaseError(error);

    if (entrada.itens) {
      console.log("🔄 [UPDATE] Deletando itens antigos da entrada:", id);
      
      // ✅ Buscar itens existentes ANTES de deletar
      const { data: itensExistentes, error: fetchError } = await supabase
        .from("estoque_entrada_item")
        .select("entrada_item_id")
        .eq("entrada_id", id);

      if (fetchError) {
        console.error("❌ [UPDATE] Erro ao buscar itens existentes:", fetchError);
        handleSupabaseError(fetchError);
      }

      console.log(`📊 [UPDATE] Encontrados ${itensExistentes?.length || 0} itens para deletar`);

      // ✅ Deletar itens existentes
      const { data: deleteData, error: deleteError } = await supabase
        .from("estoque_entrada_item")
        .delete()
        .eq("entrada_id", id)
        .select(); // ✅ Retornar itens deletados

      if (deleteError) {
        console.error("❌ [UPDATE] Erro ao deletar itens:", deleteError);
        handleSupabaseError(deleteError);
      }

      console.log(`✅ [UPDATE] ${deleteData?.length || 0} itens deletados com sucesso`);

      // ✅ Verificar se DELETE funcionou
      if (itensExistentes && itensExistentes.length > 0 && (!deleteData || deleteData.length === 0)) {
        console.error("⚠️ [UPDATE] DELETE não removeu os itens existentes!");
        throw new Error("Falha ao deletar itens existentes. Verifique as permissões (RLS).");
      }

      if (entrada.itens.length > 0) {
        console.log("🔄 [UPDATE] Inserindo novos itens:", entrada.itens.length);
        const tenantId = await getCurrentTenantId();
        
        const itensData = entrada.itens.map((item: any) => ({
          entrada_item_id: item.id, // ✅ UUID gerado no frontend
          entrada_id: id,
          tenant_id: tenantId,
          produto_id: item.produto_id,
          produto_nome: item.produto_nome,
          quantidade_compra: item.quantidade_compra,
          custo_unitario_compra: item.custo_unitario_compra,
          outras_despesas_item: item.outras_despesas_item,
          lote: item.lote,
          validade: item.validade,
          localizacao: item.localizacao,
          observacao_item: item.observacao_item,
          marca_id: item.marca || null,
          forma_pagamento: item.forma_pagamento || null,
          tipo_embalagem: item.tipo_embalagem,
          peso_individual_g: item.peso_individual_g || null,
          volume_individual_ml: item.volume_individual_ml || null,
        }));

        const { error: itensError } = await supabase
          .from("estoque_entrada_item")
          .insert(itensData);
        
        if (itensError) {
          console.error("❌ [UPDATE] Erro ao inserir novos itens:", itensError);
          handleSupabaseError(itensError);
        }
        console.log("✅ [UPDATE] Novos itens inseridos com sucesso");
      }
    }
  },

  async delete(id: string) {
    console.log("🗑️ [EntradaAdapter] Iniciando exclusão da entrada:", id);
    
    // ✅ DELETE CASCADE + triggers cuidam de tudo automaticamente
    console.log("🗑️ [EntradaAdapter] Deletando entrada (CASCADE irá deletar itens)...");
    const { error } = await supabase
      .from("estoque_entrada")
      .delete()
      .eq("entrada_id", id);

    if (error) {
      console.error("❌ [EntradaAdapter] Erro ao deletar entrada:", error);
      handleSupabaseError(error);
    }
    
    console.log("✅ [EntradaAdapter] Entrada e itens deletados com sucesso via CASCADE");
    console.log("✅ [EntradaAdapter] Triggers de estoque executados automaticamente");
  },

  // Novos métodos para CRUD simplificado
  async createCabecalho(cabecalho: any) {
    const { data: userData } = await supabase.auth.getUser();
    const tenantId = await getCurrentTenantId();

    const { data, error } = await supabase
      .from("estoque_entrada")
      .insert({
        tenant_id: tenantId,
        tipo_movimento: cabecalho.tipo_movimento,
        data_entrada: cabecalho.data_entrada,
        fornecedor_nome: cabecalho.fornecedor_nome || null,
        documento_tipo: cabecalho.documento_tipo || null,
        documento_numero: cabecalho.documento_numero || null,
        documento_serie: cabecalho.documento_serie || null,
        chave_nfe: cabecalho.chave_nfe || null,
        observacao: cabecalho.observacao || null,
        outras_despesas_total: 0,
        total_geral_documento: 0,
        created_by: userData.user?.id,
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return { id: data.entrada_id };
  },

  async createItem(entrada_id: string, item: any) {
    const tenantId = await getCurrentTenantId();

    const { data, error } = await supabase
      .from("estoque_entrada_item")
      .insert({
        entrada_id,
        tenant_id: tenantId,
        produto_id: item.produto_id,
        produto_nome: item.produto_nome,
        quantidade_compra: item.quantidade_compra,
        custo_unitario_compra: item.custo_unitario_compra,
        outras_despesas_item: item.outras_despesas_item || null,
        lote: item.lote || null,
        validade: item.validade || null,
        localizacao: item.localizacao || null,
        observacao_item: item.observacao_item || null,
        marca_id: item.marca || null,
        forma_pagamento: item.forma_pagamento || null,
        tipo_embalagem: item.tipo_embalagem,
        peso_individual_g: item.peso_individual_g || null,
        volume_individual_ml: item.volume_individual_ml || null,
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    
    // Atualizar totais da entrada
    await entradaAdapter.atualizarTotalEntrada(entrada_id);
    
    return { id: data.entrada_item_id };
  },

  async updateItem(entrada_item_id: string, item: any) {
    const { error } = await supabase
      .from("estoque_entrada_item")
      .update({
        produto_id: item.produto_id,
        produto_nome: item.produto_nome,
        quantidade_compra: item.quantidade_compra,
        custo_unitario_compra: item.custo_unitario_compra,
        outras_despesas_item: item.outras_despesas_item || null,
        lote: item.lote || null,
        validade: item.validade || null,
        localizacao: item.localizacao || null,
        observacao_item: item.observacao_item || null,
        marca_id: item.marca || null,
        forma_pagamento: item.forma_pagamento || null,
        tipo_embalagem: item.tipo_embalagem,
        peso_individual_g: item.peso_individual_g || null,
        volume_individual_ml: item.volume_individual_ml || null,
      })
      .eq("entrada_item_id", entrada_item_id);

    if (error) handleSupabaseError(error);
    
    // Buscar entrada_id do item
    const { data: itemData } = await supabase
      .from("estoque_entrada_item")
      .select("entrada_id")
      .eq("entrada_item_id", entrada_item_id)
      .single();
    
    if (itemData) {
      await entradaAdapter.atualizarTotalEntrada(itemData.entrada_id);
    }
  },

  async deleteItem(entrada_item_id: string) {
    // Buscar dados completos do item antes de deletar
    const { data: itemData } = await supabase
      .from("estoque_entrada_item")
      .select("entrada_id, produto_id, tenant_id")
      .eq("entrada_item_id", entrada_item_id)
      .single();

    // Deletar o item
    const { error } = await supabase
      .from("estoque_entrada_item")
      .delete()
      .eq("entrada_item_id", entrada_item_id);

    if (error) handleSupabaseError(error);
    
    // Atualizar totais da entrada
    if (itemData) {
      await entradaAdapter.atualizarTotalEntrada(itemData.entrada_id);
      
      // Forçar recálculo do estoque
      await supabase.rpc('recalcular_saldo_produto', {
        p_produto_id: itemData.produto_id,
        p_tenant_id: itemData.tenant_id
      });
    }
  },

  async getItens(entrada_id: string) {
    const { data, error } = await supabase
      .from("estoque_entrada_item")
      .select("*")
      .eq("entrada_id", entrada_id)
      .order("created_at", { ascending: true });

    if (error) handleSupabaseError(error);
    return data || [];
  },

  async atualizarTotalEntrada(entrada_id: string) {
    // Buscar todos os itens da entrada
    const { data: itens } = await supabase
      .from("estoque_entrada_item")
      .select("quantidade_compra, custo_unitario_compra, outras_despesas_item")
      .eq("entrada_id", entrada_id);

    if (!itens || itens.length === 0) {
      // Se não há itens, zerar totais
      await supabase
        .from("estoque_entrada")
        .update({
          outras_despesas_total: 0,
          total_geral_documento: 0,
        })
        .eq("entrada_id", entrada_id);
      return;
    }

    // Calcular totais
    let totalGeral = 0;
    let totalOutrasDespesas = 0;

    itens.forEach((item: any) => {
      const custoItem = item.custo_unitario_compra * item.quantidade_compra;
      const outrasDespesas = item.outras_despesas_item || 0;
      totalGeral += custoItem + outrasDespesas;
      totalOutrasDespesas += outrasDespesas;
    });

    // Atualizar entrada
    const { error } = await supabase
      .from("estoque_entrada")
      .update({
        outras_despesas_total: totalOutrasDespesas,
        total_geral_documento: totalGeral,
      })
      .eq("entrada_id", entrada_id);

    if (error) handleSupabaseError(error);
  },
};

export const ingredientsAdapter: IngredientsPort = {
  list: notImplemented,
  get: notImplemented,
  create: notImplemented,
  update: notImplemented,
  addStock: notImplemented,
};

export const usersAdapter: UsersPort = {
  me: notImplemented,
  login: notImplemented,
  logout: notImplemented,
  list: notImplemented,
  create: notImplemented,
  update: notImplemented,
};

export const reportsAdapter: ReportsPort = {
  sales: notImplemented,
  production: notImplemented,
  stock: notImplemented,
};

// ============================================================================
// ESTOQUE ADAPTER
// ============================================================================

export const estoqueAdapter: EstoquePort = {
  async list(options?: QueryOptions): Promise<ListResponse<StockItem>> {
    const {
      page = 1,
      limit = 100,
      search = "",
      sort = "produto_nome",
      order = "asc",
      filters = {},
    } = options || {};

    const tenantId = await getCurrentTenantId();

    try {
      // PASSO 1: Buscar todas as categorias do tenant (para cache/lookup)
      const { data: categoriasData } = await supabase
        .from("categoria")
        .select("id_categoria, nome")
        .eq("tenant_id", tenantId)
        .eq("ativa", true);

      const categoriasMap = new Map<string, string>(
        (categoriasData || []).map((c) => [c.id_categoria, c.nome])
      );

      // PASSO 2: Query de estoque base
      let query = supabase
        .from("estoque_saldo")
        .select(`
          id,
          produto_id,
          quantidade_atual,
          custo_medio_unitario,
          valor_total_estoque,
          updated_at,
          tenant_id,
          produto:produto_id(
            nome,
            id_categoria,
            unidade_padrao
          )
        `, { count: "exact" })
        .eq("tenant_id", tenantId);

      // Ordenação simples por quantidade (sempre funciona)
      query = query.order("quantidade_atual", { ascending: false });

      // PASSO 2.5: Buscar todas as entradas primeiro
      const { data: entradasFinalizadas } = await supabase
        .from("estoque_entrada")
        .select("entrada_id")
        .eq("tenant_id", tenantId);

      const entradaIds = (entradasFinalizadas || []).map(e => e.entrada_id);

      // PASSO 2.6: Buscar detalhes agregados de peso/volume/embalagem
      const stockDetailsMap = new Map<string, {
        peso_total_g: number;
        volume_total_ml: number;
        tipo_embalagem: string | null;
      }>();

      if (entradaIds.length > 0) {
        const { data: stockDetailsData } = await supabase
          .from("estoque_entrada_item")
          .select(`
            produto_id,
            quantidade_compra,
            peso_individual_g,
            volume_individual_ml,
            tipo_embalagem
          `)
          .eq("tenant_id", tenantId)
          .in("entrada_id", entradaIds);

        (stockDetailsData || []).forEach((detail: any) => {
          const current = stockDetailsMap.get(detail.produto_id) || {
            peso_total_g: 0,
            volume_total_ml: 0,
            tipo_embalagem: null,
          };

          // Somar peso total (peso_individual_g * quantidade_compra)
          if (detail.peso_individual_g && detail.quantidade_compra) {
            current.peso_total_g += detail.peso_individual_g * detail.quantidade_compra;
          }

          // Somar volume total (volume_individual_ml * quantidade_compra)
          if (detail.volume_individual_ml && detail.quantidade_compra) {
            current.volume_total_ml += detail.volume_individual_ml * detail.quantidade_compra;
          }

          // Usar última embalagem encontrada
          if (detail.tipo_embalagem) {
            current.tipo_embalagem = detail.tipo_embalagem;
          }

          stockDetailsMap.set(detail.produto_id, current);
        });
      }

      // Paginação
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error("🔴 ERRO REAL DO SUPABASE:", JSON.stringify(error, null, 2));
        console.error("🔴 ERRO MESSAGE:", error.message);
        console.error("🔴 ERRO CODE:", error.code);
        console.error("🔴 ERRO DETAILS:", error.details);
        console.error("🔴 ERRO HINT:", error.hint);
        handleSupabaseError(error);
      }

      // PASSO 3: Mapear resultado e injetar nome da categoria + detalhes de estoque
      let items: StockItem[] = (data || []).map((row: any) => {
        const stockDetails = stockDetailsMap.get(row.produto_id);
        
        return {
          id: row.id,
          produto_id: row.produto_id,
          produto_nome: row.produto?.nome || "Produto sem nome",
          categoria: categoriasMap.get(row.produto?.id_categoria) || null,
          id_categoria: row.produto?.id_categoria || null,
          quantidade_atual: row.quantidade_atual,
          tipo_embalagem: stockDetails?.tipo_embalagem || null,
          peso_total_g: stockDetails?.peso_total_g || null,
          volume_total_ml: stockDetails?.volume_total_ml || null,
          custo_medio_temporal: row.custo_medio_unitario,
          valor_total_estoque: row.valor_total_estoque,
          data_ultima_atualizacao: row.updated_at,
          tenant_id: row.tenant_id,
        };
      });

      // PASSO 4: Aplicar filtro de categoria NO CÓDIGO
      if (filters.categoria) {
        items = items.filter(item => item.categoria === filters.categoria);
      }

      // PASSO 5: Aplicar busca por nome OU categoria NO CÓDIGO
      if (search) {
        const searchLower = search.toLowerCase();
        items = items.filter(
          (item) =>
            item.produto_nome.toLowerCase().includes(searchLower) ||
            (item.categoria && item.categoria.toLowerCase().includes(searchLower))
        );
      }

      // PASSO 6: Ordenação completa NO CÓDIGO
      items.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sort) {
          case "produto_nome":
            aVal = a.produto_nome;
            bVal = b.produto_nome;
            break;
          case "categoria":
            aVal = a.categoria || "";
            bVal = b.categoria || "";
            break;
          case "quantidade_atual":
            aVal = a.quantidade_atual;
            bVal = b.quantidade_atual;
            break;
          case "custo_medio_temporal":
            aVal = a.custo_medio_temporal;
            bVal = b.custo_medio_temporal;
            break;
          case "valor_total_estoque":
            aVal = a.valor_total_estoque;
            bVal = b.valor_total_estoque;
            break;
          default:
            aVal = a.produto_nome;
            bVal = b.produto_nome;
        }

        if (typeof aVal === "number" && typeof bVal === "number") {
          return order === "asc" ? aVal - bVal : bVal - aVal;
        }

        const comparison = String(aVal).localeCompare(String(bVal));
        return order === "asc" ? comparison : -comparison;
      });

      return {
        items,
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error("Erro ao listar estoque:", error);
      handleSupabaseError(error);
      throw error; // Garantir que a exceção seja propagada
    }
  },

  async get(id: string): Promise<StockItem | null> {
    const tenantId = await getCurrentTenantId();

    try {
      // PASSO 1: Buscar produto do estoque
      const { data, error } = await supabase
        .from("estoque_saldo")
        .select(`
          id,
          produto_id,
          quantidade_atual,
          custo_medio_unitario,
          valor_total_estoque,
          updated_at,
          tenant_id,
          produto:produto_id(
            nome,
            id_categoria,
            unidade_padrao
          )
        `)
        .eq("produto_id", id)
        .eq("tenant_id", tenantId)
        .maybeSingle();

      if (error) handleSupabaseError(error);
      if (!data) return null;

      // PASSO 2: Buscar nome da categoria (se existir)
      let categoriaNome: string | null = null;
      if (data.produto?.id_categoria) {
        const { data: catData } = await supabase
          .from("categoria")
          .select("nome")
          .eq("id_categoria", data.produto.id_categoria)
          .eq("tenant_id", tenantId)
          .eq("ativa", true)
          .maybeSingle();

        categoriaNome = catData?.nome || null;
      }

      // PASSO 3: Mapear para StockItem
      return {
        id: data.id,
        produto_id: data.produto_id,
        produto_nome: data.produto?.nome || "Produto sem nome",
        categoria: categoriaNome,
        id_categoria: data.produto?.id_categoria || null,
        quantidade_atual: data.quantidade_atual,
        tipo_embalagem: null,
        peso_total_g: null,
        volume_total_ml: null,
        custo_medio_temporal: data.custo_medio_unitario,
        valor_total_estoque: data.valor_total_estoque,
        data_ultima_atualizacao: data.updated_at,
        tenant_id: data.tenant_id,
      } as StockItem;
    } catch (error) {
      console.error("Erro ao buscar item de estoque:", error);
      handleSupabaseError(error);
      return null;
    }
  },

  async getHistory(produtoId: string): Promise<MovementHistory[]> {
    const { data, error } = await supabase
      .from("estoque_entrada_item")
      .select(
        `
        entrada_item_id,
        produto_id,
        produto_nome,
        quantidade_compra,
        tipo_embalagem,
        peso_individual_g,
        volume_individual_ml,
        custo_unitario_compra,
        lote,
        validade,
        created_at,
        entrada:estoque_entrada!inner(
          entrada_id,
          data_entrada,
          tipo_movimento,
          fornecedor_nome,
          documento_tipo,
          documento_numero
        )
      `
      )
      .eq("produto_id", produtoId)
      .order("created_at", { ascending: false });

    if (error) handleSupabaseError(error);

    // Mapear para o formato esperado pelo MovementHistory
    return (data || []).map((item: any) => ({
      entrada_item_id: item.entrada_item_id,
      produto_id: item.produto_id,
      produto_nome: item.produto_nome,
      quantidade_estoque_calculada: item.quantidade_compra,
      tipo_embalagem: item.tipo_embalagem,
      peso_individual_g: item.peso_individual_g,
      volume_individual_ml: item.volume_individual_ml,
      custo_unitario_final_base: item.custo_unitario_compra,
      lote: item.lote,
      validade: item.validade,
      created_at: item.created_at,
      entrada: item.entrada,
    }));
  },

  async getExpiring(daysThreshold: number = 30): Promise<any[]> {
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + daysThreshold * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0];

    const { data, error } = await supabase
      .from("estoque_entrada_item")
      .select(
        `
        produto_id,
        produto_nome,
        lote,
        validade,
        quantidade_compra,
        entrada_id
      `
      )
      .not("validade", "is", null)
      .gte("validade", today)
      .lte("validade", futureDate)
      .order("validade", { ascending: true });

    if (error) handleSupabaseError(error);

    return data || [];
  },
};

// ============================================================================
// TIPO PRODUTOS ADAPTER
// ============================================================================

export const tipoProdutosAdapter: TipoProdutosPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "nome",
      order = "asc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("tipo_produto")
      .select("*", { count: "exact" });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,categoria.ilike.%${search}%`);
    }

    if (filters.categoria) {
      query = query.eq("categoria", filters.categoria);
    }

    if (filters.ativo !== undefined) {
      query = query.eq("ativo", filters.ativo);
    }

    query = query.order(sort, { ascending: order === "asc" });

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("tipo_produto")
      .select("*")
      .eq("id_tipo_produto", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    return data;
  },

  async create(input: any) {
    const { data, error } = await supabase
      .from("tipo_produto")
      .insert({
        ...input,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    console.log("🔧 [TipoProdutoAdapter] Atualizando produto:", { id, input });
    
    const { data, error } = await supabase
      .from("tipo_produto")
      .update({
        ...input,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .eq("id_tipo_produto", id)
      .select()
      .single();

    if (error) {
      console.error("❌ [TipoProdutoAdapter] Erro no UPDATE:", error);
      handleSupabaseError(error);
    }
    
    console.log("✅ [TipoProdutoAdapter] Produto atualizado com sucesso:", data);
    return data;
  },

  async delete(id: string) {
    console.log("🗑️ [TipoProdutoAdapter] Deletando tipo produto:", id);
    const { error } = await supabase
      .from("tipo_produto")
      .delete()
      .eq("id_tipo_produto", id);

    if (error) {
      console.error("❌ [TipoProdutoAdapter] Erro:", error);
      if (error.code === "23503") {
        throw new Error("Este produto não pode ser excluído pois possui vendas associadas.");
      }
      handleSupabaseError(error);
    }
    console.log("✅ [TipoProdutoAdapter] Tipo produto deletado");
  },
};

// ============================================================================
// ESTABELECIMENTOS ADAPTER
// ============================================================================

export const estabelecimentosAdapter: EstabelecimentosPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "nome",
      order = "asc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("estabelecimento")
      .select("*", { count: "exact" });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,cnpj.ilike.%${search}%`);
    }

    if (filters.ativo !== undefined) {
      query = query.eq("ativo", filters.ativo);
    }

    query = query.order(sort, { ascending: order === "asc" });

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("estabelecimento")
      .select("*")
      .eq("id_estabelecimento", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    return data;
  },

  async create(input: any) {
    const tenantId = await getCurrentTenantId();
    
    const { data, error } = await supabase
      .from("estabelecimento")
      .insert({
        ...input,
        tenant_id: tenantId,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { error } = await supabase
      .from("estabelecimento")
      .update({
        ...input,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .eq("id_estabelecimento", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    console.log("🗑️ [EstabelecimentoAdapter] Deletando estabelecimento:", id);
    const { error } = await supabase
      .from("estabelecimento")
      .delete()
      .eq("id_estabelecimento", id);

    if (error) {
      console.error("❌ [EstabelecimentoAdapter] Erro:", error);
      if (error.code === "23503") {
        throw new Error("Este estabelecimento não pode ser excluído pois possui vendas ou entradas associadas.");
      }
      handleSupabaseError(error);
    }
    console.log("✅ [EstabelecimentoAdapter] Estabelecimento deletado");
  },
};

// ============================================================================
// CLIENTES ADAPTER
// ============================================================================

export const clientesAdapter: ClientesPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "nome",
      order = "asc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("cliente")
      .select("*", { count: "exact" });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,cpf_cnpj.ilike.%${search}%,telefone.ilike.%${search}%`);
    }

    if (filters.ativo !== undefined) {
      query = query.eq("ativo", filters.ativo);
    }

    query = query.order(sort, { ascending: order === "asc" });

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("cliente")
      .select("*")
      .eq("id_cliente", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    return data;
  },

  async create(input: any) {
    const tenantId = await getCurrentTenantId();
    
    const cleanedInput = Object.entries(input).reduce((acc, [key, value]) => {
      acc[key] = value === "" ? null : value;
      return acc;
    }, {} as any);

    const { data, error } = await supabase
      .from("cliente")
      .insert({
        ...cleanedInput,
        tenant_id: tenantId,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const cleanedInput = Object.entries(input).reduce((acc, [key, value]) => {
      acc[key] = value === "" ? null : value;
      return acc;
    }, {} as any);

    const { error } = await supabase
      .from("cliente")
      .update({
        ...cleanedInput,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .eq("id_cliente", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    console.log("🗑️ [ClienteAdapter] Deletando cliente:", id);
    const { error } = await supabase
      .from("cliente")
      .delete()
      .eq("id_cliente", id);

    if (error) {
      console.error("❌ [ClienteAdapter] Erro:", error);
      if (error.code === "23503") {
        throw new Error("Este cliente não pode ser excluído pois possui vendas associadas.");
      }
      handleSupabaseError(error);
    }
    console.log("✅ [ClienteAdapter] Cliente deletado");
  },
};

// ============================================================================
// VENDAS ADAPTER
// ============================================================================

// Helper para calcular subtotal de item
function calculateItemSubtotal(
  quantidade: number,
  preco_unitario: number,
  desconto_valor: number,
  brinde: boolean
): number {
  if (brinde) return 0;
  const subtotal = quantidade * preco_unitario - desconto_valor;
  return Math.max(0, subtotal);
}

export const vendasAdapter: VendasPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "created_at",
      order = "desc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("venda")
      .select(
        `
        *,
        cliente:cliente_id (nome),
        estabelecimento:estabelecimento_id (nome),
        venda_item!venda_item_id_venda_fkey (
          quantidade,
          tipo_produto:tipo_produto_id (
            preco_custo
          )
        )
      `,
        { count: "exact" }
      );

    if (search) {
      query = query.or(
        `numero_venda.eq.${search},numero_pedido.ilike.%${search}%`
      );
    }

    if (filters.status_pagamento) {
      query = query.eq("status_pagamento", filters.status_pagamento);
    }

    if (filters.forma_pagamento) {
      query = query.eq("forma_pagamento", filters.forma_pagamento);
    }

    if (filters.data_inicio && filters.data_fim) {
      query = query
        .gte("data_venda", filters.data_inicio)
        .lte("data_venda", filters.data_fim);
    }

    query = query.order(sort, { ascending: order === "asc" });

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    // Calcular margem de lucro e lucro em R$ para cada venda
    const vendasComMargem = (data || []).map((venda: any) => {
      // Verificar se TODOS os itens têm preco_custo cadastrado
      const todosItemsTemCusto = (venda.venda_item || []).every((item: any) => 
        item.tipo_produto?.preco_custo != null
      );

      let margem = null;
      let lucroValor = null;

      if (todosItemsTemCusto && (venda.venda_item || []).length > 0) {
        const custoTotal = (venda.venda_item || []).reduce((acc: number, item: any) => {
          return acc + (item.quantidade * item.tipo_produto.preco_custo);
        }, 0);
        
        const receitaLiquida = venda.valor_total || 0;
        margem = receitaLiquida > 0 
          ? ((receitaLiquida - custoTotal) / receitaLiquida) * 100 
          : 0;
        
        // Calcular lucro em R$
        lucroValor = receitaLiquida - custoTotal;
      }
      
      // Remove venda_item do retorno (não precisa no list)
      const { venda_item, ...vendaSemItens } = venda;
      
      return {
        ...vendaSemItens,
        margem_lucro: margem,
        lucro_valor: lucroValor
      };
    });

    return {
      items: vendasComMargem,
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data: venda, error: vendaError } = await supabase
      .from("venda")
      .select(
        `
        *,
        cliente:cliente_id (nome),
        estabelecimento:estabelecimento_id (nome)
      `
      )
      .eq("id_venda", id)
      .maybeSingle();

    if (vendaError) handleSupabaseError(vendaError);
    if (!venda) return null;

    const { data: itens, error: itensError } = await supabase
      .from("venda_item")
      .select("*")
      .eq("id_venda", id)
      .order("created_at", { ascending: true });

    if (itensError) handleSupabaseError(itensError);

    return {
      ...venda,
      itens: itens || [],
    };
  },

  async create(venda: any) {
    const user = (await supabase.auth.getUser()).data.user;

    // Validar autenticação
    if (!user?.id) {
      throw new Error("Usuário não autenticado. Por favor, faça login novamente.");
    }

    // Helper para limpar campos vazios
    const cleanOptionalFields = (data: any) => {
      const cleaned = { ...data };
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === "") {
          cleaned[key] = null;
        }
      });
      return cleaned;
    };

    // Calcular totais
    const subtotal = venda.itens.reduce(
      (sum: number, item: any) => sum + item.subtotal,
      0
    );
    const valor_total =
      subtotal - (venda.desconto_total || 0) + (venda.valor_frete || 0);

    // Limpar dados da venda
    const vendaLimpa = cleanOptionalFields({
      data_venda: venda.data_venda,
      cliente_id: venda.cliente_id,
      estabelecimento_id: venda.estabelecimento_id,
      forma_pagamento: venda.forma_pagamento,
      status_pagamento: venda.status_pagamento || "Pendente",
      observacoes: venda.observacoes,
      responsavel_venda: venda.responsavel_venda,
      responsavel_registro: user.id,
      canal_venda: venda.canal_venda,
      observacoes_gerais: venda.observacoes_gerais,
      numero_pedido: venda.numero_pedido,
      codigo_fiscal_cupom: venda.codigo_fiscal_cupom,
      data_entrega: venda.data_entrega,
      situacao_entrega: venda.situacao_entrega,
      subtotal,
      desconto_total: venda.desconto_total || 0,
      valor_frete: venda.valor_frete || 0,
      valor_total,
      created_by: user.id,
    });

    // Criar venda
    const { data: vendaData, error: vendaError } = await supabase
      .from("venda")
      .insert(vendaLimpa)
      .select()
      .single();

    if (vendaError) handleSupabaseError(vendaError);

    // Criar itens
    const itensToInsert = venda.itens.map((item: any) => 
      cleanOptionalFields({
        id_venda: vendaData.id_venda,
        tipo_produto_id: item.tipo_produto_id,
        produto_nome: item.produto_nome,
        quantidade: item.quantidade,
        unidade_medida: item.unidade_medida,
        preco_unitario: item.preco_unitario,
        desconto_percentual: item.desconto_percentual || 0,
        desconto_valor: item.desconto_valor || 0,
        brinde: item.brinde || false,
        subtotal: item.subtotal,
        observacoes_item: item.observacoes_item,
      })
    );

    const { error: itensError } = await supabase
      .from("venda_item")
      .insert(itensToInsert);

    // Rollback: Se erro nos itens, deletar venda criada
    if (itensError) {
      await supabase.from("venda").delete().eq("id_venda", vendaData.id_venda);
      throw new Error(`Erro ao inserir itens: ${itensError.message}`);
    }

    return { id: vendaData.id_venda };
  },

  async update(id: string, venda: any) {
    const user = (await supabase.auth.getUser()).data.user;

    // Validar autenticação
    if (!user?.id) {
      throw new Error("Usuário não autenticado. Por favor, faça login novamente.");
    }

    // Helper para limpar campos vazios
    const cleanOptionalFields = (data: any) => {
      const cleaned = { ...data };
      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === "") {
          cleaned[key] = null;
        }
      });
      return cleaned;
    };

    // Calcular totais
    const subtotal = venda.itens.reduce(
      (sum: number, item: any) => sum + item.subtotal,
      0
    );
    const valor_total =
      subtotal - (venda.desconto_total || 0) + (venda.valor_frete || 0);

    // Limpar dados da venda
    const vendaLimpa = cleanOptionalFields({
      data_venda: venda.data_venda,
      cliente_id: venda.cliente_id,
      estabelecimento_id: venda.estabelecimento_id,
      forma_pagamento: venda.forma_pagamento,
      status_pagamento: venda.status_pagamento,
      observacoes: venda.observacoes,
      responsavel_venda: venda.responsavel_venda,
      canal_venda: venda.canal_venda,
      observacoes_gerais: venda.observacoes_gerais,
      numero_pedido: venda.numero_pedido,
      codigo_fiscal_cupom: venda.codigo_fiscal_cupom,
      data_entrega: venda.data_entrega,
      situacao_entrega: venda.situacao_entrega,
      subtotal,
      desconto_total: venda.desconto_total || 0,
      valor_frete: venda.valor_frete || 0,
      valor_total,
      updated_by: user.id,
    });

    // Atualizar venda
    const { error: vendaError } = await supabase
      .from("venda")
      .update(vendaLimpa)
      .eq("id_venda", id);

    if (vendaError) handleSupabaseError(vendaError);

    // Deletar itens antigos
    const { error: deleteError } = await supabase
      .from("venda_item")
      .delete()
      .eq("id_venda", id);

    if (deleteError) handleSupabaseError(deleteError);

    // Criar novos itens
    const itensToInsert = venda.itens.map((item: any) => 
      cleanOptionalFields({
        id_venda: id,
        tipo_produto_id: item.tipo_produto_id,
        produto_nome: item.produto_nome,
        quantidade: item.quantidade,
        unidade_medida: item.unidade_medida,
        preco_unitario: item.preco_unitario,
        desconto_percentual: item.desconto_percentual || 0,
        desconto_valor: item.desconto_valor || 0,
        brinde: item.brinde || false,
        subtotal: item.subtotal,
        observacoes_item: item.observacoes_item,
      })
    );

    const { error: itensError } = await supabase
      .from("venda_item")
      .insert(itensToInsert);

    if (itensError) handleSupabaseError(itensError);
  },

  async delete(id: string) {
    console.log("🗑️ [VendasAdapter] Iniciando exclusão da venda:", id);
    
    // 1. Deletar itens da venda primeiro (relacionamento)
    console.log("📦 [VendasAdapter] Deletando itens da venda...");
    const { error: itensError } = await supabase
      .from("venda_item")
      .delete()
      .eq("id_venda", id);

    if (itensError) {
      console.error("❌ [VendasAdapter] Erro ao deletar itens:", itensError);
      throw new Error(`Erro ao deletar itens da venda: ${itensError.message}`);
    }
    console.log("✅ [VendasAdapter] Itens deletados com sucesso");

    // 2. Deletar a venda
    console.log("🗑️ [VendasAdapter] Deletando venda...");
    const { error: vendaError } = await supabase
      .from("venda")
      .delete()
      .eq("id_venda", id);

    if (vendaError) {
      console.error("❌ [VendasAdapter] Erro ao deletar venda:", vendaError);
      throw new Error(`Erro ao deletar venda: ${vendaError.message}`);
    }
    
    console.log("✅ [VendasAdapter] Venda deletada com sucesso");
  },

  async getSummary(period: 'hoje' | 'semana' | 'mes' | 'ano') {
    const now = new Date();
    let startDate: string;

    switch (period) {
      case 'hoje':
        startDate = now.toISOString().split('T')[0];
        break;
      case 'semana':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDate = weekAgo.toISOString().split('T')[0];
        break;
      case 'mes':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .split('T')[0];
        break;
      case 'ano':
        startDate = new Date(now.getFullYear(), 0, 1)
          .toISOString()
          .split('T')[0];
        break;
    }

    // Buscar vendas do período
    const { data: vendas, error: vendasError } = await supabase
      .from("venda")
      .select("id_venda, valor_total, data_venda")
      .gte("data_venda", startDate);

    if (vendasError) handleSupabaseError(vendasError);

    const quantidade_vendas = vendas?.length || 0;
    const valor_total = vendas?.reduce((sum, v) => sum + Number(v.valor_total), 0) || 0;

    // Buscar quantidade de itens vendidos no período
    const vendaIds = vendas?.map(v => v.id_venda) || [];
    
    let quantidade_itens = 0;
    if (vendaIds.length > 0) {
      const { data: itens, error: itensError } = await supabase
        .from("venda_item")
        .select("quantidade")
        .in("id_venda", vendaIds);

      if (itensError) handleSupabaseError(itensError);

      quantidade_itens = itens?.reduce((sum, item) => sum + Number(item.quantidade), 0) || 0;
    }

    return {
      periodo: period,
      quantidade_vendas,
      quantidade_itens,
      valor_total,
    };
  },
};

// ============================================================================
// RECEITAS ADAPTER
// ============================================================================

export const receitasAdapter: ReceitasPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "created_at",
      order = "desc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("receita")
      .select("*, ingredientes_count:receita_ingrediente(count)", { count: "exact" });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,categoria.ilike.%${search}%`);
    }

    if (filters.categoria) {
      query = query.eq("categoria", filters.categoria);
    }

    if (filters.ativo !== undefined) {
      query = query.eq("ativo", filters.ativo);
    }

    query = query.order(sort, { ascending: order === "asc" });
    const from = (page - 1) * limit;
    query = query.range(from, from + limit - 1);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data: receita, error: receitaError } = await supabase
      .from("receita")
      .select("*")
      .eq("id_receita", id)
      .maybeSingle();

    if (receitaError) handleSupabaseError(receitaError);
    if (!receita) return null;

    const { data: ingredientes, error: ingredientesError } = await supabase
      .from("receita_ingrediente")
      .select("*")
      .eq("id_receita", id)
      .order("created_at");

    if (ingredientesError) handleSupabaseError(ingredientesError);

    return {
      receita,
      ingredientes: ingredientes || [],
    };
  },

  async create(data: any) {
    const { receita, ingredientes } = data;
    const { data: userData } = await supabase.auth.getUser();

    if (!userData.user) {
      throw new Error("Usuário não autenticado");
    }

    // Buscar tenant_id do usuário (CRÍTICO para multi-tenancy)
    const { data: tenantData, error: tenantError } = await supabase
      .from("tenant_users")
      .select("tenant_id")
      .eq("user_id", userData.user.id)
      .single();

    if (tenantError || !tenantData) {
      throw new Error("Usuário não pertence a nenhum tenant");
    }

    // Remover campo ingredientes do payload da receita (não existe na tabela)
    const { ingredientes: _, ...receitaData } = receita;

    const { data: receitaInserted, error: receitaError } = await supabase
      .from("receita")
      .insert({
        ...receitaData,
        tenant_id: tenantData.tenant_id,
        created_by: userData.user.id,
      })
      .select("id_receita")
      .single();

    if (receitaError) handleSupabaseError(receitaError);

    if (ingredientes?.length > 0) {
      const ingredientesData = ingredientes.map((i: any) => ({
        ...i,
        id_receita: receitaInserted.id_receita,
      }));

      const { error: ingredientesError } = await supabase
        .from("receita_ingrediente")
        .insert(ingredientesData);

      if (ingredientesError) handleSupabaseError(ingredientesError);
    }

    return { id: receitaInserted.id_receita };
  },

  async update(id: string, data: any) {
    const { receita, ingredientes } = data;
    const { data: userData, error: authError } = await supabase.auth.getUser();

    // Validar autenticação (igual ao create)
    if (authError || !userData.user) {
      console.error("❌ Erro de autenticação ao editar receita:", authError);
      throw new Error("Usuário não autenticado. Faça login e tente novamente.");
    }

    // Remover campos que não devem ser atualizados
    const { 
      id_receita,      // Chave primária (não atualizar)
      created_at,      // Data de criação (não atualizar)
      created_by,      // Usuário criador (não atualizar)
      ...receitaData   // Todos os outros campos são permitidos (inclui tenant_id para RLS)
    } = receita;

    const { data: updateResult, error: receitaError } = await supabase
      .from("receita")
      .update({
        ...receitaData,
        updated_by: userData.user.id,
        updated_at: new Date().toISOString(), // Garantir timestamp atualizado
      })
      .eq("id_receita", id)
      .select();

    if (receitaError) {
      handleSupabaseError(receitaError);
    }

    // Verificar se alguma linha foi atualizada (RLS pode bloquear silenciosamente)
    if (!updateResult || updateResult.length === 0) {
      throw new Error("Nenhuma receita foi atualizada. Verifique as permissões ou se a receita existe.");
    }

    // Deletar ingredientes existentes e reinserir
    await supabase
      .from("receita_ingrediente")
      .delete()
      .eq("id_receita", id);

    if (ingredientes?.length > 0) {
      const ingredientesData = ingredientes.map((i: any) => ({
        ...i,
        id_receita: id,
      }));

      const { error: ingredientesError } = await supabase
        .from("receita_ingrediente")
        .insert(ingredientesData);

      if (ingredientesError) handleSupabaseError(ingredientesError);
    }
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("receita")
      .delete()
      .eq("id_receita", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// TIPO MOVIMENTO ENTRADA ADAPTER
// ============================================================================

export const tipoMovimentoEntradaAdapter = {
  async list(options?: QueryOptions) {
    const { search = "" } = options || {};

    let query = supabase
      .from("tipo_movimento_entrada")
      .select("*", { count: "exact" })
      .order("ordem", { ascending: true });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,codigo.ilike.%${search}%`);
    }

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return { items: data || [], total: count || 0, page: 1, limit: 100 };
  },

  async create(input: any) {
    const tenant_id = await getCurrentTenantId();
    
    const { data: maxOrdemData } = await supabase
      .from("tipo_movimento_entrada")
      .select("ordem")
      .eq("tenant_id", tenant_id)
      .order("ordem", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    const proximaOrdem = (maxOrdemData?.ordem ?? 0) + 1;
    
    const { data, error } = await supabase
      .from("tipo_movimento_entrada")
      .insert({ 
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
        descricao: input.descricao?.trim(),
        ordem: proximaOrdem,
        ativo: input.ativo !== undefined ? input.ativo : true,
        tenant_id 
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { error } = await supabase
      .from("tipo_movimento_entrada")
      .update({
        ...input,
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
      })
      .eq("id_tipo_movimento", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("tipo_movimento_entrada")
      .delete()
      .eq("id_tipo_movimento", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// TIPO DOCUMENTO ADAPTER
// ============================================================================

export const tipoDocumentoAdapter = {
  async list(options?: QueryOptions) {
    const { search = "" } = options || {};

    let query = supabase
      .from("tipo_documento")
      .select("*", { count: "exact" })
      .order("ordem", { ascending: true });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,codigo.ilike.%${search}%`);
    }

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return { items: data || [], total: count || 0, page: 1, limit: 100 };
  },

  async create(input: any) {
    const tenant_id = await getCurrentTenantId();
    
    const { data: maxOrdemData } = await supabase
      .from("tipo_documento")
      .select("ordem")
      .eq("tenant_id", tenant_id)
      .order("ordem", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    const proximaOrdem = (maxOrdemData?.ordem ?? 0) + 1;
    
    const { data, error } = await supabase
      .from("tipo_documento")
      .insert({ 
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
        descricao: input.descricao?.trim(),
        ordem: proximaOrdem,
        ativo: input.ativo !== undefined ? input.ativo : true,
        tenant_id 
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { error } = await supabase
      .from("tipo_documento")
      .update({
        ...input,
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
      })
      .eq("id_tipo_documento", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("tipo_documento")
      .delete()
      .eq("id_tipo_documento", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// FORMA PAGAMENTO ADAPTER
// ============================================================================

export const formaPagamentoAdapter = {
  async list(options?: QueryOptions) {
    const { search = "" } = options || {};

    let query = supabase
      .from("forma_pagamento")
      .select("*", { count: "exact" })
      .order("ordem", { ascending: true });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,codigo.ilike.%${search}%`);
    }

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return { items: data || [], total: count || 0, page: 1, limit: 100 };
  },

  async create(input: any) {
    const tenant_id = await getCurrentTenantId();
    
    const { data: maxOrdemData } = await supabase
      .from("forma_pagamento")
      .select("ordem")
      .eq("tenant_id", tenant_id)
      .order("ordem", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    const proximaOrdem = (maxOrdemData?.ordem ?? 0) + 1;
    
    const { data, error } = await supabase
      .from("forma_pagamento")
      .insert({ 
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
        descricao: input.descricao?.trim(),
        ordem: proximaOrdem,
        ativo: input.ativo !== undefined ? input.ativo : true,
        tenant_id 
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { error } = await supabase
      .from("forma_pagamento")
      .update({
        ...input,
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
      })
      .eq("id_forma_pagamento", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("forma_pagamento")
      .delete()
      .eq("id_forma_pagamento", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// CATEGORIA RECEITA ADAPTER
// ============================================================================

export const categoriaReceitaAdapter = {
  async list(options?: QueryOptions) {
    const { search = "" } = options || {};

    let query = supabase
      .from("categoria_receita")
      .select("*", { count: "exact" })
      .order("ordem", { ascending: true });

    if (search) {
      query = query.ilike("nome", `%${search}%`);
    }

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return { items: data || [], total: count || 0, page: 1, limit: 100 };
  },

  async create(input: any) {
    const tenant_id = await getCurrentTenantId();
    
    const { data: maxOrdemData } = await supabase
      .from("categoria_receita")
      .select("ordem")
      .eq("tenant_id", tenant_id)
      .order("ordem", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    const proximaOrdem = (maxOrdemData?.ordem ?? 0) + 1;
    
    const { data, error } = await supabase
      .from("categoria_receita")
      .insert({ 
        nome: input.nome?.trim(),
        descricao: input.descricao?.trim(),
        ordem: proximaOrdem,
        ativo: input.ativo !== undefined ? input.ativo : true,
        tenant_id 
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { error } = await supabase
      .from("categoria_receita")
      .update({
        ...input,
        nome: input.nome?.trim(),
      })
      .eq("id_categoria_receita", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("categoria_receita")
      .delete()
      .eq("id_categoria_receita", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// NIVEL DIFICULDADE ADAPTER
// ============================================================================

export const nivelDificuldadeAdapter = {
  async list(options?: QueryOptions) {
    const { search = "" } = options || {};

    let query = supabase
      .from("nivel_dificuldade")
      .select("*", { count: "exact" })
      .order("ordem", { ascending: true });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,codigo.ilike.%${search}%`);
    }

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return { items: data || [], total: count || 0, page: 1, limit: 100 };
  },

  async create(input: any) {
    const tenant_id = await getCurrentTenantId();
    
    const { data: maxOrdemData } = await supabase
      .from("nivel_dificuldade")
      .select("ordem")
      .eq("tenant_id", tenant_id)
      .order("ordem", { ascending: false })
      .limit(1)
      .maybeSingle();
    
    const proximaOrdem = (maxOrdemData?.ordem ?? 0) + 1;
    
    const { data, error } = await supabase
      .from("nivel_dificuldade")
      .insert({ 
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
        descricao: input.descricao?.trim(),
        ordem: proximaOrdem,
        ativo: input.ativo !== undefined ? input.ativo : true,
        tenant_id 
      })
      .select()
      .single();

    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, input: any) {
    const { error } = await supabase
      .from("nivel_dificuldade")
      .update({
        ...input,
        codigo: input.codigo?.trim(),
        nome: input.nome?.trim(),
      })
      .eq("id_nivel_dificuldade", id);

    if (error) handleSupabaseError(error);
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("nivel_dificuldade")
      .delete()
      .eq("id_nivel_dificuldade", id);

    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// FORNADAS ADAPTER
// ============================================================================

export const fornadasAdapter: FornadasPort = {
  async list(options?: QueryOptions) {
    const {
      page = 1,
      limit = 50,
      search = "",
      sort = "data_fornada",
      order = "desc",
      filters = {},
    } = options || {};

    let query = supabase
      .from("fornada")
      .select("*", { count: "exact" });

    if (search) {
      query = query.or(`responsavel.ilike.%${search}%,observacoes_gerais.ilike.%${search}%`);
    }

    if (filters.data_inicial) {
      query = query.gte("data_fornada", filters.data_inicial);
    }
    if (filters.data_final) {
      query = query.lte("data_fornada", filters.data_final);
    }

    query = query.order(sort, { ascending: order === "asc" });

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const { data, error } = await supabase
      .from("fornada")
      .select("*")
      .eq("id_fornada", id)
      .maybeSingle();

    if (error) handleSupabaseError(error);
    return data;
  },

  async create(input: any) {
    const tenantId = await getCurrentTenantId();
    const userId = (await supabase.auth.getUser()).data.user?.id;

    // Gerar número sequencial da fornada
    const { data: numeroData, error: numeroError } = await supabase.rpc('gerar_numero_fornada', {
      p_tenant_id: tenantId
    });
    
    if (numeroError) handleSupabaseError(numeroError);

    const { data: fornadaData, error: fornadaError } = await supabase
      .from("fornada")
      .insert({
        tenant_id: tenantId,
        numero_fornada: numeroData,
        data_fornada: input.data_fornada,
        responsavel: input.responsavel,
        destino: input.destino,
        observacoes_gerais: input.observacoes_gerais,
        rendimento_total: input.rendimento_total,
        tempo_total_minutos: input.tempo_total_minutos,
        valor_hora_profissional: input.valor_hora_profissional,
        custo_mao_obra: input.custo_mao_obra,
        custo_gas: input.custo_gas,
        tipo_gas: input.tipo_gas,
        outras_despesas: input.outras_despesas,
        custo_total: input.custo_total || 0,
        custo_medio_unitario: input.custo_medio_unitario || 0,
        debitar_estoque: input.debitar_estoque ?? true,
        criado_por: userId,
        origem_app: 'web',
      })
      .select()
      .single();

    if (fornadaError) handleSupabaseError(fornadaError);

    // Inserir itens
    if (input.itens && input.itens.length > 0) {
      const itensPayload = input.itens.map((item: any) => ({
        id_fornada: fornadaData.id_fornada,
        id_receita: item.id_receita,
        tenant_id: tenantId,
        receita_nome: item.receita_nome,
        receita_rendimento: item.receita_rendimento,
        receita_custo_total: item.receita_custo_total,
        quantidade_produzida: item.quantidade_produzida,
        custo_unitario_fixado: item.custo_unitario_fixado,
        custo_total_item: item.custo_total_item,
        observacoes_item: item.observacoes_item,
        criado_por: userId,
      }));

      const { error: itensError } = await supabase
        .from("fornada_item")
        .insert(itensPayload);

      if (itensError) handleSupabaseError(itensError);
    }

    // Inserir custos adicionais
    if (input.custos_adicionais && input.custos_adicionais.length > 0) {
      const custosPayload = input.custos_adicionais.map((custo: any) => ({
        id_fornada: fornadaData.id_fornada,
        tenant_id: tenantId,
        tipo_produto_id: custo.tipo_produto_id,
        descricao: custo.descricao,
        quantidade: custo.quantidade,
        valor_unitario: custo.valor_unitario,
        valor_total: custo.valor_total,
        criado_por: userId,
      }));

      const { error: custosError } = await supabase
        .from("fornada_custo_adicional")
        .insert(custosPayload);

      if (custosError) handleSupabaseError(custosError);
    }

    // Debitar estoque (se configurado)
    if (input.debitar_estoque) {
      const { error: debitoError } = await supabase.rpc("debitar_estoque_fornada", {
        p_id_fornada: fornadaData.id_fornada,
      });
      if (debitoError) handleSupabaseError(debitoError);
    }

    return { id: fornadaData.id_fornada };
  },

  async update(id: string, input: any) {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const tenantId = await getCurrentTenantId();

    // Verificar se estoque foi debitado (estorno necessário)
    const { data: fornadaAtual } = await supabase
      .from("fornada")
      .select("estoque_debitado")
      .eq("id_fornada", id)
      .single();

    // Se estoque foi debitado, fazer estorno ANTES
    if (fornadaAtual?.estoque_debitado) {
      const { error: estornoError } = await supabase.rpc("estornar_estoque_fornada", {
        p_id_fornada: id,
      });
      if (estornoError) handleSupabaseError(estornoError);
    }

    // Deletar itens antigos
    const { error: deleteItensError } = await supabase
      .from("fornada_item")
      .delete()
      .eq("id_fornada", id);

    if (deleteItensError) handleSupabaseError(deleteItensError);

    // Inserir novos itens
    if (input.itens && input.itens.length > 0) {
      const itensPayload = input.itens.map((item: any) => ({
        id_fornada: id,
        id_receita: item.id_receita,
        tenant_id: tenantId,
        receita_nome: item.receita_nome,
        receita_rendimento: item.receita_rendimento,
        receita_custo_total: item.receita_custo_total,
        quantidade_produzida: item.quantidade_produzida,
        custo_unitario_fixado: item.custo_unitario_fixado,
        custo_total_item: item.custo_total_item,
        observacoes_item: item.observacoes_item,
        criado_por: userId,
      }));

      const { error: itensError } = await supabase
        .from("fornada_item")
        .insert(itensPayload);

      if (itensError) handleSupabaseError(itensError);
    }

    // Deletar custos adicionais antigos
    const { error: deleteCustosError } = await supabase
      .from("fornada_custo_adicional")
      .delete()
      .eq("id_fornada", id);

    if (deleteCustosError) handleSupabaseError(deleteCustosError);

    // Inserir novos custos adicionais
    if (input.custos_adicionais && input.custos_adicionais.length > 0) {
      const custosPayload = input.custos_adicionais.map((custo: any) => ({
        id_fornada: id,
        tenant_id: tenantId,
        tipo_produto_id: custo.tipo_produto_id,
        descricao: custo.descricao,
        quantidade: custo.quantidade,
        valor_unitario: custo.valor_unitario,
        valor_total: custo.valor_total,
        criado_por: userId,
      }));

      const { error: custosError } = await supabase
        .from("fornada_custo_adicional")
        .insert(custosPayload);

      if (custosError) handleSupabaseError(custosError);
    }

    // Atualizar cabeçalho
    const { error: updateError } = await supabase
      .from("fornada")
      .update({
        data_fornada: input.data_fornada,
        responsavel: input.responsavel,
        destino: input.destino,
        observacoes_gerais: input.observacoes_gerais,
        rendimento_total: input.rendimento_total,
        tempo_total_minutos: input.tempo_total_minutos,
        valor_hora_profissional: input.valor_hora_profissional,
        custo_mao_obra: input.custo_mao_obra,
        custo_gas: input.custo_gas,
        tipo_gas: input.tipo_gas,
        outras_despesas: input.outras_despesas,
        custo_total: input.custo_total || 0,
        custo_medio_unitario: input.custo_medio_unitario || 0,
        debitar_estoque: input.debitar_estoque ?? true,
        estoque_debitado: false, // Reset flag
        atualizado_por: userId,
        dt_ultima_atualizacao: new Date().toISOString(),
      })
      .eq("id_fornada", id);

    if (updateError) handleSupabaseError(updateError);

    // Debitar estoque com novos valores (se configurado)
    if (input.debitar_estoque) {
      const { error: debitoError } = await supabase.rpc("debitar_estoque_fornada", {
        p_id_fornada: id,
      });
      if (debitoError) handleSupabaseError(debitoError);
    }
  },

  async delete(id: string) {
    // Hard delete: deletar fornada_item e depois fornada
    const { error: deleteItensError } = await supabase
      .from("fornada_item")
      .delete()
      .eq("id_fornada", id);

    if (deleteItensError) handleSupabaseError(deleteItensError);

    const { error: deleteFornadaError } = await supabase
      .from("fornada")
      .delete()
      .eq("id_fornada", id);

    if (deleteFornadaError) handleSupabaseError(deleteFornadaError);
  },

  async duplicar(id: string) {
    const tenantId = await getCurrentTenantId();
    const userId = (await supabase.auth.getUser()).data.user?.id;

    // Buscar fornada original
    const { data: fornadaOriginal, error: getError } = await supabase
      .from("fornada")
      .select("*")
      .eq("id_fornada", id)
      .single();

    if (getError) handleSupabaseError(getError);

    // Buscar itens originais
    const { data: itensOriginais, error: getItensError } = await supabase
      .from("fornada_item")
      .select("*")
      .eq("id_fornada", id);

    if (getItensError) handleSupabaseError(getItensError);

    // Criar nova fornada (rascunho)
    const { data: novaFornada, error: createError } = await supabase
      .from("fornada")
      .insert({
        tenant_id: tenantId,
        data_fornada: new Date().toISOString().split('T')[0], // Data atual
        responsavel: fornadaOriginal.responsavel,
        destino: fornadaOriginal.destino,
        observacoes_gerais: `[CÓPIA] ${fornadaOriginal.observacoes_gerais || ''}`,
        rendimento_total: fornadaOriginal.rendimento_total,
        tempo_total_minutos: fornadaOriginal.tempo_total_minutos,
        custo_total: fornadaOriginal.custo_total,
        custo_medio_unitario: fornadaOriginal.custo_medio_unitario,
        debitar_estoque: true,
        criado_por: userId,
        origem_app: 'web',
      })
      .select()
      .single();

    if (createError) handleSupabaseError(createError);

    // Duplicar itens
    if (itensOriginais && itensOriginais.length > 0) {
      const itensPayload = itensOriginais.map((item: any) => ({
        id_fornada: novaFornada.id_fornada,
        id_receita: item.id_receita,
        tenant_id: tenantId,
        receita_nome: item.receita_nome,
        receita_rendimento: item.receita_rendimento,
        receita_custo_total: item.receita_custo_total,
        quantidade_produzida: item.quantidade_produzida,
        custo_unitario_fixado: item.custo_unitario_fixado,
        custo_total_item: item.custo_total_item,
        observacoes_item: item.observacoes_item,
        criado_por: userId,
      }));

      const { error: itensError } = await supabase
        .from("fornada_item")
        .insert(itensPayload);

      if (itensError) handleSupabaseError(itensError);
    }

    return { id: novaFornada.id_fornada };
  },

  async getItens(id_fornada: string) {
    const { data, error } = await supabase
      .from("fornada_item")
      .select("*")
      .eq("id_fornada", id_fornada)
      .order("dt_cadastro", { ascending: true });

    if (error) handleSupabaseError(error);
    return data || [];
  },
};

// ============================================================================
// FORNECEDOR ADAPTER
// ============================================================================

async function getCurrentUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuário não autenticado");
  return user.id;
}

export const fornecedorAdapter = {
  async list(options: QueryOptions = {}) {
    const tenantId = await getCurrentTenantId();
    const { page = 1, limit = 50, search = "", filters = {} } = options;

    let query = supabase
      .from("fornecedor")
      .select("*", { count: "exact" })
      .eq("tenant_id", tenantId)
      .order("ordem", { ascending: true })
      .order("nome", { ascending: true });

    if (search) {
      query = query.or(`nome.ilike.%${search}%,cnpj_cpf.ilike.%${search}%`);
    }

    if (filters.ativo !== undefined) {
      query = query.eq("ativo", filters.ativo);
    }

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const tenantId = await getCurrentTenantId();
    const { data, error } = await supabase
      .from("fornecedor")
      .select("*")
      .eq("id_fornecedor", id)
      .eq("tenant_id", tenantId)
      .single();
    if (error) handleSupabaseError(error);
    return data;
  },

  async create(fornecedor: any) {
    const tenantId = await getCurrentTenantId();
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from("fornecedor")
      .insert({ ...fornecedor, tenant_id: tenantId, created_by: userId, updated_by: userId })
      .select()
      .single();
    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, fornecedor: any) {
    const tenantId = await getCurrentTenantId();
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from("fornecedor")
      .update({ ...fornecedor, updated_by: userId, updated_at: new Date().toISOString() })
      .eq("id_fornecedor", id)
      .eq("tenant_id", tenantId)
      .select()
      .single();
    if (error) handleSupabaseError(error);
    return data;
  },

  async delete(id: string) {
    const tenantId = await getCurrentTenantId();
    const { error } = await supabase
      .from("fornecedor")
      .delete()
      .eq("id_fornecedor", id)
      .eq("tenant_id", tenantId);
    if (error) handleSupabaseError(error);
  },
};

// ============================================================================
// MARCA ADAPTER
// ============================================================================

export const marcaAdapter = {
  async list(options: QueryOptions = {}) {
    const tenantId = await getCurrentTenantId();
    const { page = 1, limit = 50, search = "", filters = {} } = options;

    let query = supabase
      .from("marca")
      .select("*", { count: "exact" })
      .eq("tenant_id", tenantId)
      .order("ordem", { ascending: true })
      .order("nome", { ascending: true });

    if (search) {
      query = query.ilike("nome", `%${search}%`);
    }

    if (filters.ativo !== undefined) {
      query = query.eq("ativo", filters.ativo);
    }

    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) handleSupabaseError(error);

    return {
      items: data || [],
      total: count || 0,
      page,
      limit,
    };
  },

  async get(id: string) {
    const tenantId = await getCurrentTenantId();
    const { data, error } = await supabase
      .from("marca")
      .select("*")
      .eq("id_marca", id)
      .eq("tenant_id", tenantId)
      .single();
    if (error) handleSupabaseError(error);
    return data;
  },

  async create(marca: any) {
    const tenantId = await getCurrentTenantId();
    const { data, error } = await supabase
      .from("marca")
      .insert({ ...marca, tenant_id: tenantId })
      .select()
      .single();
    if (error) handleSupabaseError(error);
    return data;
  },

  async update(id: string, marca: any) {
    const tenantId = await getCurrentTenantId();
    const { data, error } = await supabase
      .from("marca")
      .update({ ...marca, updated_at: new Date().toISOString() })
      .eq("id_marca", id)
      .eq("tenant_id", tenantId)
      .select()
      .single();
    if (error) handleSupabaseError(error);
    return data;
  },

  async delete(id: string) {
    const tenantId = await getCurrentTenantId();
    const { error } = await supabase
      .from("marca")
      .delete()
      .eq("id_marca", id)
      .eq("tenant_id", tenantId);
    if (error) handleSupabaseError(error);
  },
};

export default {
  productsAdapter,
  stockAdapter,
  batchesAdapter,
  salesAdapter,
  ingredientsAdapter,
  usersAdapter,
  reportsAdapter,
  categoriesAdapter,
  unitsAdapter,
  entradaAdapter,
  estoqueAdapter,
  tipoProdutosAdapter,
  estabelecimentosAdapter,
  clientesAdapter,
  vendasAdapter,
  receitasAdapter,
  fornadasAdapter,
  tipoMovimentoEntradaAdapter,
  tipoDocumentoAdapter,
  formaPagamentoAdapter,
  categoriaReceitaAdapter,
  nivelDificuldadeAdapter,
  fornecedorAdapter,
  marcaAdapter,
};
